import { useEffect, useState } from 'react';
import { TrendingUp, Zap, Activity, CheckCircle2 } from 'lucide-react';
import { useAppStore } from '../../store/appStore';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  suffix?: string;
}

function AnimatedCounter({ end, duration = 1000, suffix = '' }: { end: number; duration?: number; suffix?: string }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(easeOutQuart * end);

      setCount(currentCount);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [end, duration]);

  return (
    <span>
      {count.toLocaleString()}
      {suffix}
    </span>
  );
}

function StatCard({ title, value, icon, trend, suffix = '' }: StatCardProps) {
  return (
    <div className="bg-recipe-card border border-recipe-border rounded-xl p-6 hover:border-recipe-accent transition-all duration-300 hover:shadow-lg group">
      <div className="flex items-start justify-between mb-4">
        <div className="p-3 bg-recipe-accent/10 rounded-lg group-hover:bg-recipe-accent/20 transition-colors">
          {icon}
        </div>
        {trend && (
          <div
            className={`flex items-center gap-1 text-xs font-medium ${
              trend.isPositive ? 'text-green-500' : 'text-red-500'
            }`}
          >
            <TrendingUp
              className={`w-3 h-3 ${trend.isPositive ? '' : 'rotate-180'}`}
            />
            {trend.value}%
          </div>
        )}
      </div>

      <div className="space-y-1">
        <div className="text-3xl font-bold text-recipe-text">
          <AnimatedCounter end={value} suffix={suffix} />
        </div>
        <div className="text-sm text-recipe-text-muted">{title}</div>
      </div>
    </div>
  );
}

export default function QuickStats() {
  const { apps, aiInteractionsToday, activeSessions } = useAppStore();

  const activeAppsCount = apps.filter((app) => app.status === 'active').length;

  // Calculate system health (simplified)
  const systemHealth = Math.min(
    100,
    Math.round((activeAppsCount / apps.length) * 100)
  );

  const stats = [
    {
      title: 'Available Tools',
      value: apps.length,
      icon: <Activity className="w-5 h-5 text-recipe-accent" />,
      trend: {
        value: 12,
        isPositive: true
      }
    },
    {
      title: 'People Online Now',
      value: activeSessions,
      icon: <Zap className="w-5 h-5 text-recipe-accent" />,
      trend: {
        value: 8,
        isPositive: true
      }
    },
    {
      title: 'Tasks Done Today',
      value: aiInteractionsToday,
      icon: <TrendingUp className="w-5 h-5 text-recipe-accent" />,
      trend: {
        value: 23,
        isPositive: true
      }
    },
    {
      title: 'All Systems Working',
      value: systemHealth,
      icon: <CheckCircle2 className="w-5 h-5 text-recipe-accent" />,
      suffix: '%'
    }
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-recipe-text">At a Glance</h2>
        <button className="text-sm text-recipe-accent hover:underline">
          See More
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div key={stat.title} style={{ animationDelay: `${index * 100}ms` }} className="animate-slide-up">
            <StatCard {...stat} />
          </div>
        ))}
      </div>
    </div>
  );
}
